import {ApiClient} from '@hconnect/apiclient'
import {queryOptions} from '@tanstack/react-query'

import type {
  HealthIndicatorsApi,
  KilnInfo,
  MetricSignalMapping,
  SignalAnomaly,
  SignalData
} from '../types'

const refetchInterval = 1000 * 60 // 1 minute

export const kmsQueries = {
  all: () => ['kiln-monitoring'] as const,

  kilnInfo: (plantUpmId: string) =>
    queryOptions({
      queryKey: [...kmsQueries.all(), 'kiln-info', plantUpmId] as const,
      queryFn: async () => {
        const path = `/kms/${plantUpmId}/kilns`
        return (await ApiClient.axiosInstance.get<KilnInfo[]>(path)).data
      },
      staleTime: 1000 * 60 * 60,
      throwOnError: (_, query) => query.state.data === undefined
    }),

  healthIndicators: (kilnId: string, plantUpmId: string) =>
    queryOptions({
      queryKey: [...kmsQueries.all(), 'health-indicators', plantUpmId, kilnId] as const,
      queryFn: async ({signal}) => {
        const path = `/kms/${plantUpmId}/signal/health-indicators`
        const response = await ApiClient.axiosInstance.get<HealthIndicatorsApi>(path, {
          params: {departmentId: kilnId},
          signal
        })
        return response.data
      },
      refetchInterval,
      throwOnError: false
    }),

  signals: (kilnId: string | undefined, plantUpmId: string) =>
    queryOptions({
      queryKey: [...kmsQueries.all(), 'sensors', plantUpmId, kilnId] as const,
      queryFn: async () => {
        const path = `/kms/${plantUpmId}/signals`
        return (
          await ApiClient.axiosInstance.get<SignalData[]>(path, {
            params: {departmentId: kilnId}
          })
        ).data
      },
      refetchInterval,
      throwOnError: (_, query) => query.state.data === undefined,
      enabled: !!kilnId
    }),

  sensorsAnomalies: (kilnId: string | undefined, plantUpmId: string) =>
    queryOptions({
      queryKey: [...kmsQueries.all(), 'sensor-anomalies', plantUpmId, kilnId] as const,
      queryFn: async () => {
        const path = `/kms/${plantUpmId}/signal/threshold-violations`
        return (
          await ApiClient.axiosInstance.get<SignalAnomaly[]>(path, {
            params: {departmentId: kilnId}
          })
        ).data
      },
      refetchInterval,
      throwOnError: (_, query) => query.state.data === undefined,
      enabled: !!kilnId
    }),

  sensorsMappings: (kilnId: string | undefined, plantUpmId: string) =>
    queryOptions({
      queryKey: [...kmsQueries.all(), 'sensor-mappings', plantUpmId, kilnId] as const,
      queryFn: async () => {
        const path = `/kms/${plantUpmId}/signal/svg-mappings`
        return (
          await ApiClient.axiosInstance.get<MetricSignalMapping[]>(path, {
            params: {departmentId: kilnId}
          })
        ).data
      },
      throwOnError: (_, query) => query.state.data === undefined,
      enabled: !!kilnId
    })
}
